import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslate } from "../i18n";
import { navGroups } from "../navigation";
import { BrandMark } from "./BrandMark";
import { brand } from "../config/brand";

interface AppSidebarProps {
    /** Rail width only; the drawer copy is always expanded. */
    collapsed?: boolean;
    onToggleCollapse?: () => void;
    /** Called after a section is picked, so the mobile drawer can close. */
    onNavigate?: () => void;
    /** `drawer` drops the sticky rail chrome and fills its container. */
    variant?: "rail" | "drawer";
}

/**
 * Primary navigation.
 *
 * Sections sit under a mono eyebrow label naming the domain they belong to, so
 * the topbar can name a location as "Clinic › Doctors". The active item carries
 * an accent rail on the sidebar's edge, which survives the collapsed state
 * where labels do not.
 */
export const AppSidebar = ({
    collapsed = false,
    onToggleCollapse,
    onNavigate,
    variant = "rail"
}: AppSidebarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const t = useTranslate();

    const isDrawer = variant === "drawer";
    const isCollapsed = !isDrawer && collapsed;
    const activeSegment = location.pathname.split("/").filter(Boolean)[0];

    const go = (route: string) => {
        navigate(`/${route}`);
        onNavigate?.();
    };

    // Index across groups so the reveal cascades down the whole rail, not
    // restarting inside each group.
    let order = 0;

    return (
        <aside
            className={`app-sidebar${isDrawer ? " app-sidebar--drawer" : ""}`}
            data-collapsed={isCollapsed}
        >
            <div className="brand">
                <span className="brand__mark">
                    <BrandMark />
                </span>
                <span className="brand__text">
                    <span className="brand__name">{brand.name}</span>
                    <span className="brand__role">{t("nav.role")}</span>
                </span>
            </div>

            <nav className="nav" aria-label={t("common.menu")}>
                {navGroups.map(group => (
                    <div className="nav__group" key={group.labelKey}>
                        <div className="nav__group-label eyebrow">{t(group.labelKey)}</div>
                        {group.items.map(item => {
                            const active = activeSegment === item.route;
                            const label = t(item.labelKey);
                            return (
                                <button
                                    key={item.route}
                                    type="button"
                                    className={`nav__item${active ? " is-active" : ""}`}
                                    style={{ animationDelay: `${28 * order++}ms` }}
                                    aria-current={active ? "page" : undefined}
                                    title={isCollapsed ? label : undefined}
                                    onClick={() => go(item.route)}
                                >
                                    {item.icon}
                                    <span className="nav__item-label">{label}</span>
                                </button>
                            );
                        })}
                    </div>
                ))}
            </nav>

            <div className="sidebar__foot">
                {onToggleCollapse != null && !isDrawer ? (
                    <button
                        type="button"
                        className="sidebar__collapse"
                        onClick={onToggleCollapse}
                        aria-label={t(isCollapsed ? "sidebar.expand" : "sidebar.collapse")}
                        title={t(isCollapsed ? "sidebar.expand" : "sidebar.collapse")}
                    >
                        {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    </button>
                ) : null}
            </div>
        </aside>
    );
};
